import { useState, useEffect, useCallback } from "react";
import { X, ArrowRight, ArrowLeft, Sparkles, Check } from "lucide-react";
import { Button } from "./ui/button";
import { useAuth } from "../App";
import axios from "axios";
import { getAuthHeaders } from "../lib/auth";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const ONBOARDING_STEPS = [
  {
    id: "welcome",
    title: "Bienvenue sur CMES Pharma Intel !",
    description: "Nous allons vous guider pour bien demarrer avec votre plateforme. Cette visite guidee ne prend que 60 secondes.",
    position: "center",
    highlight: null,
  },
  {
    id: "hub",
    title: "Votre Hub Central",
    description: "C'est votre page d'accueil. Vous y retrouvez tous vos services. Les modules actifs sont en couleur, les autres en grise.",
    position: "center",
    highlight: "[data-testid='hub-cmes-log'], [data-testid='hub-intel-stock'], [data-testid='hub-tech-conseil']",
  },
  {
    id: "notifications",
    title: "Notifications en temps reel",
    description: "La cloche vous alerte des retards de paiement, nouveaux paiements, et evenements importants. Cliquez dessus pour voir vos alertes.",
    position: "top-right",
    highlight: "[data-testid='notification-bell']",
  },
  {
    id: "sidebar",
    title: "Navigation laterale",
    description: "Utilisez le menu lateral pour naviguer entre les fonctionnalites de chaque service. Factures, pipeline, stocks, scanner IA...",
    position: "left",
    highlight: "[data-testid='sidebar-nav'], nav",
  },
  {
    id: "plan",
    title: "Votre abonnement",
    description: "Vous pouvez changer de formule a tout moment. Pendant l'essai gratuit de 30 jours, testez toutes les fonctionnalites de votre plan.",
    position: "center",
    highlight: null,
  },
  {
    id: "done",
    title: "Vous etes pret !",
    description: "C'est parti ! Explorez votre plateforme. En cas de question, contactez-nous a contact@cmes-pharma.org ou au 07 68 12 70 00.",
    position: "center",
    highlight: null,
  },
];

export default function OnboardingGuide() {
  const { user, refreshUser } = useAuth();
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(false);
  const [highlightRect, setHighlightRect] = useState(null);

  useEffect(() => {
    if (!user) return;
    // Show only for pharmacy/etablissement users who haven't completed onboarding
    if (!["pharmacy", "etablissement"].includes(user.role)) return;
    if (user.onboarding_completed) return;

    // Check localStorage too (in case backend hasn't saved yet)
    const dismissed = localStorage.getItem(`cmes_onboarding_${user.id}`);
    if (dismissed) return;

    // Small delay so the page renders first
    const timer = setTimeout(() => setVisible(true), 1500);
    return () => clearTimeout(timer);
  }, [user]);

  // Highlight element
  useEffect(() => {
    if (!visible) return;
    const s = ONBOARDING_STEPS[step];
    if (s?.highlight) {
      const el = document.querySelector(s.highlight);
      if (el) {
        const rect = el.getBoundingClientRect();
        setHighlightRect({ top: rect.top - 8, left: rect.left - 8, width: rect.width + 16, height: rect.height + 16 });
        return;
      }
    }
    setHighlightRect(null);
  }, [step, visible]);

  const finish = useCallback(async () => {
    setVisible(false);
    localStorage.setItem(`cmes_onboarding_${user.id}`, "true");
    try {
      await axios.post(`${API}/auth/onboarding-complete`, {}, { headers: getAuthHeaders() });
      await refreshUser();
    } catch {}
  }, [user, refreshUser]);

  const next = () => {
    if (step >= ONBOARDING_STEPS.length - 1) {
      finish();
    } else {
      setStep(step + 1);
    }
  };

  const prev = () => {
    if (step > 0) setStep(step - 1);
  };

  if (!visible) return null;

  const currentStep = ONBOARDING_STEPS[step];
  const isLast = step === ONBOARDING_STEPS.length - 1;
  const isFirst = step === 0;
  const progress = ((step + 1) / ONBOARDING_STEPS.length) * 100;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 z-[9990] bg-black/40 backdrop-blur-[1px] transition-opacity duration-300" data-testid="onboarding-overlay" />

      {/* Highlight cutout */}
      {highlightRect && (
        <div
          className="fixed z-[9991] border-2 border-sky-400 rounded-xl shadow-[0_0_0_9999px_rgba(0,0,0,0.4)] pointer-events-none transition-all duration-500"
          style={{
            top: highlightRect.top,
            left: highlightRect.left,
            width: highlightRect.width,
            height: highlightRect.height,
          }}
        />
      )}

      {/* Tooltip */}
      <div
        className="fixed z-[9992] max-w-md w-[90vw] bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden transition-all duration-300"
        style={{
          top: "50%", left: "50%", transform: "translate(-50%, -50%)",
        }}
        data-testid="onboarding-tooltip"
      >
        {/* Progress bar */}
        <div className="h-1 bg-slate-100">
          <div className="h-full bg-gradient-to-r from-sky-500 to-emerald-500 transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>

        <div className="p-6">
          {/* Close button */}
          <button onClick={finish} className="absolute top-3 right-3 text-slate-400 hover:text-slate-600 transition-colors" data-testid="onboarding-skip">
            <X className="w-5 h-5" />
          </button>

          {/* Icon */}
          <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center mb-4">
            {isLast ? <Check className="w-5 h-5 text-emerald-500" /> : <Sparkles className="w-5 h-5 text-sky-500" />}
          </div>

          {/* Content */}
          <h3 className="text-lg font-bold text-slate-900 mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            {currentStep.title}
          </h3>
          <p className="text-sm text-slate-600 leading-relaxed mb-6">
            {currentStep.description}
          </p>

          {/* Actions */}
          <div className="flex items-center justify-between">
            <div className="flex gap-1.5">
              {ONBOARDING_STEPS.map((_, i) => (
                <div key={i} className={`w-2 h-2 rounded-full transition-all ${i === step ? "bg-sky-500 w-6" : i < step ? "bg-sky-300" : "bg-slate-200"}`} />
              ))}
            </div>

            <div className="flex gap-2">
              {!isFirst && (
                <Button onClick={prev} variant="outline" size="sm" className="text-xs gap-1 text-slate-600 border-slate-200" data-testid="onboarding-prev">
                  <ArrowLeft className="w-3 h-3" /> Retour
                </Button>
              )}
              <Button onClick={next} size="sm" className="text-xs gap-1 bg-sky-600 hover:bg-sky-700 text-white" data-testid="onboarding-next">
                {isLast ? "C'est parti !" : "Suivant"} {!isLast && <ArrowRight className="w-3 h-3" />}
              </Button>
            </div>
          </div>

          {/* Skip link */}
          {!isLast && (
            <button onClick={finish} className="text-[10px] text-slate-400 hover:text-slate-600 mt-3 block mx-auto" data-testid="onboarding-skip-link">
              Passer la visite guidee
            </button>
          )}
        </div>
      </div>
    </>
  );
}
